import type { Money } from '@/shared/api/types';

/**
 * Formatacao de apresentacao.
 *
 * O backend entrega dinheiro como inteiro na menor unidade, acompanhado da moeda e
 * do expoente, e percentual em base de pontos, porque ponto flutuante nao
 * representa decimal exato. A conversao para leitura acontece aqui, e apenas aqui:
 * espalhar divisoes por cem pelo codigo faz com que uma delas eventualmente use o
 * divisor errado.
 */

/** Moeda unica operada hoje. Ver docs/API.md secao 8. */
export const BRL = 'BRL';

/**
 * Casas decimais por moeda.
 *
 * A leitura usa o expoente que o servidor enviou junto do valor. Este mapa serve
 * apenas ao caminho inverso, quando a tela monta um valor a partir do que a pessoa
 * digitou e ainda nao tem um expoente vindo do servidor.
 */
const expoentes: Record<string, number> = { BRL: 2 };

function expoenteDe(currency: string): number {
  return expoentes[currency] ?? 2;
}

const dataCurta = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

const dataHora = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/**
 * Formatadores por moeda, criados sob demanda.
 *
 * O valor chega com a propria moeda. Fixar o real no formatador faria a tela
 * mostrar o simbolo errado no dia em que outra moeda aparecer, sem nenhum sinal de
 * que algo mudou.
 */
const formatadores = new Map<string, Intl.NumberFormat>();
const formatadoresCompactos = new Map<string, Intl.NumberFormat>();

function formatador(currency: string, compacto: boolean): Intl.NumberFormat {
  const cache = compacto ? formatadoresCompactos : formatadores;
  const existente = cache.get(currency);
  if (existente) return existente;

  const criado = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency,
    ...(compacto ? { notation: 'compact' as const, maximumFractionDigits: 1 } : {}),
  });
  cache.set(currency, criado);
  return criado;
}

/** Simbolo da moeda como a formatacao o escreve ("R$" para o real). */
export function currencySymbol(currency: string = BRL): string {
  const partes = formatador(currency, false).formatToParts(0);
  return partes.find((parte) => parte.type === 'currency')?.value ?? currency;
}

/** Converte o montante inteiro para a escala decimal da moeda. */
function decimal(value: Money): number {
  return value.amount / 10 ** value.exponent;
}

/** Converte um valor monetario em texto legivel. */
export function formatMoney(value: Money | null | undefined): string {
  if (!value) return '';
  return formatador(value.currency, false).format(decimal(value));
}

/** Versao compacta, para indicadores onde o espaco e curto. */
export function formatMoneyCompact(value: Money | null | undefined): string {
  if (!value) return '';
  return formatador(value.currency, true).format(decimal(value));
}

/** Converte base de pontos em percentual legivel. */
export function formatRate(basisPoints: number): string {
  return `${(basisPoints / 100).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}%`;
}

/**
 * Converte o percentual digitado em pontos-base, ou devolve null quando o texto nao
 * representa uma taxa exata.
 *
 * O servidor guarda taxa em pontos-base inteiros (ADR 0004): a menor taxa e 0,01%.
 * A conversao antiga multiplicava por cem em ponto flutuante e arredondava, e 0,125%
 * virava 13 pontos-base sem aviso a quem digitou. Taxa com mais de duas casas nao
 * cabe em ponto-base inteiro e e recusada, porque arredondar taxa e decisao de
 * regra, e nao da tela. A conta e feita sobre os digitos, sem ponto flutuante.
 *
 * Aceita virgula ou ponto como separador decimal e o sinal de percentual no fim. O
 * sinal negativo existe para os agravos e descontos da precificacao.
 */
export function parseRate(value: string): number | null {
  let texto = value.trim().replace(/%$/, '').trim();
  if (texto === '') return null;

  const negativo = texto.startsWith('-');
  if (negativo) texto = texto.slice(1);
  texto = texto.replace(',', '.');

  const partes = texto.split('.');
  if (partes.length > 2) return null;
  const [inteiros = '', fracao = ''] = partes;
  if (!/^\d+$/.test(inteiros) || (partes.length === 2 && !/^\d+$/.test(fracao))) {
    return null;
  }
  if (fracao.length > 2) return null;

  const pontos = Number(inteiros) * 100 + Number(fracao.padEnd(2, '0'));
  if (!Number.isSafeInteger(pontos)) return null;
  if (pontos === 0) return 0;
  return negativo ? -pontos : pontos;
}

/**
 * Mensagem do campo de taxa, ou undefined quando o texto e valido. Campo vazio nao
 * e erro aqui: obrigatoriedade e outra regra.
 */
export function rateInputError(value: string): string | undefined {
  if (value.trim() === '' || parseRate(value) !== null) return undefined;
  return 'Taxa invalida. Use ate duas casas decimais, como em 1,25 (a menor taxa e 0,01%).';
}

/**
 * Converte o percentual digitado em pontos-base, devolvendo zero quando o texto nao
 * e uma taxa valida. O formulario impede o envio de texto invalido com
 * `rateInputError`; o zero serve a previa.
 */
export function rateToBasisPoints(value: string): number {
  return parseRate(value) ?? 0;
}

/**
 * Converte pontos-base no texto que a tela preenche na edicao: virgula decimal e sem
 * zeros sobrando ("1,5" para 150, "0,05" para 5). Reabrir e salvar devolve a mesma
 * taxa ao servidor.
 */
export function rateToInput(basisPoints: number): string {
  if (!Number.isSafeInteger(basisPoints)) return '';
  const negativo = basisPoints < 0;
  const digitos = String(Math.abs(basisPoints)).padStart(3, '0');
  const inteiros = digitos.slice(0, -2);
  const fracao = digitos.slice(-2).replace(/0+$/, '');
  return `${negativo ? '-' : ''}${inteiros}${fracao ? `,${fracao}` : ''}`;
}

/**
 * Converte o texto digitado em valor monetario, ou devolve null quando o texto nao
 * representa um valor exato na moeda.
 *
 * Segue a mesma regra do servidor (`money.ParseDecimal`): havendo virgula, ela e o
 * separador decimal e os pontos sao separador de milhar; sem virgula, o ponto e o
 * separador decimal. O ponto como decimal chega por caminhos legitimos: o texto
 * que a propria tela preenche na edicao, o campo numerico do navegador e quem
 * digita no habito do ponto. Tratar todo ponto como milhar multiplicava esses
 * valores por dez ou por cem.
 *
 * A conta e feita sobre os digitos, sem ponto flutuante. Casa decimal alem do que
 * a moeda admite e recusada, e nao arredondada: o valor de garantia nao pode mudar
 * sem que a pessoa veja.
 */
export function parseMoney(value: string, currency: string = BRL): Money | null {
  const exponent = expoenteDe(currency);
  let texto = value.trim();
  if (texto === '') return null;

  const negativo = texto.startsWith('-');
  if (negativo) texto = texto.slice(1);

  if (texto.includes(',')) {
    texto = texto.replace(/\./g, '').replace(/,/g, '.');
  }

  const partes = texto.split('.');
  if (partes.length > 2) return null;

  const inteiro = partes[0] === '' ? '0' : (partes[0] ?? '0');
  const fracao = partes[1] ?? '';
  if (fracao.length > exponent) return null;

  const digitos = inteiro + fracao.padEnd(exponent, '0');
  if (!/^\d+$/.test(digitos)) return null;

  const absoluto = Number(digitos);
  if (!Number.isSafeInteger(absoluto)) return null;

  return {
    amount: negativo && absoluto !== 0 ? -absoluto : absoluto,
    currency,
    exponent,
  };
}

/**
 * Mensagem para o campo de dinheiro cujo texto nao pode ser convertido.
 *
 * Campo vazio nao e erro aqui: a obrigatoriedade e decidida por cada formulario.
 */
export function moneyInputError(
  value: string,
  currency: string = BRL,
): string | undefined {
  if (value.trim() === '' || parseMoney(value, currency) !== null) return undefined;
  return 'Valor invalido. Use virgula para os centavos, como em 1.500,50 ou 1500,50.';
}

/**
 * Converte o texto digitado em valor monetario, devolvendo zero quando o texto nao
 * e um valor valido.
 *
 * O zero existe para quem monta previa ou payload; o formulario impede o envio de
 * texto invalido com `moneyInputError`, para que um valor recusado nunca chegue ao
 * servidor disfarcado de zero.
 */
export function toMoney(value: string, currency: string = BRL): Money {
  return parseMoney(value, currency) ?? zeroMoney(currency);
}

/**
 * Devolve o texto editavel de um valor, para preencher um campo de formulario.
 *
 * O texto sai no formato que `parseMoney` le de volta sem ambiguidade: virgula
 * decimal e nenhum separador de milhar. A conta e feita sobre os digitos do
 * inteiro, e nao por divisao, para nao passar por ponto flutuante.
 */
export function moneyToInput(value: Money | null | undefined): string {
  if (!value || value.amount === 0) return '';
  const exponent = value.exponent;
  const digitos = String(Math.abs(value.amount)).padStart(exponent + 1, '0');
  const inteiro = exponent > 0 ? digitos.slice(0, -exponent) : digitos;
  const fracao = exponent > 0 ? digitos.slice(-exponent) : '';
  const texto = /^0*$/.test(fracao) ? inteiro : `${inteiro},${fracao}`;
  return value.amount < 0 ? `-${texto}` : texto;
}

/**
 * Aplica uma taxa em base de pontos com a mesma regra do servidor
 * (`Money.ApplyRate`): meio para cima em valor absoluto.
 *
 * Serve a previa que a tela mostra antes de gravar. A conta usa inteiro de
 * precisao arbitraria para que o produto de valor por taxa nunca perca digitos,
 * e a previa nunca diverge do que o servidor vai calcular.
 */
export function applyRate(value: Money, basisPoints: number): Money {
  if (value.amount === 0 || basisPoints === 0 || !Number.isSafeInteger(basisPoints)) {
    return { ...value, amount: 0 };
  }
  const produto = BigInt(value.amount) * BigInt(basisPoints);
  const denominador = 10_000n;
  let quociente = produto / denominador;
  const resto = produto % denominador;
  const restoAbsoluto = resto < 0n ? -resto : resto;
  if (restoAbsoluto * 2n >= denominador) {
    quociente += produto < 0n ? -1n : 1n;
  }
  return { ...value, amount: Number(quociente) };
}

/** Valor zerado na moeda indicada, para campo ainda nao preenchido. */
export function zeroMoney(currency: string = BRL): Money {
  return { amount: 0, currency, exponent: expoenteDe(currency) };
}

/**
 * Tamanho de arquivo em unidades de 1024, com uma casa a partir do megabyte.
 *
 * Um backup vai de kilobytes a gigabytes, e a casa decimal so ajuda quando a
 * diferenca entre dois arquivos parecidos esta nela.
 */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  const unidades = ['KB', 'MB', 'GB', 'TB'];
  let valor = bytes / 1024;
  let indice = 0;
  while (valor >= 1024 && indice < unidades.length - 1) {
    valor /= 1024;
    indice += 1;
  }
  const casas = indice === 0 ? 0 : 1;
  return `${valor.toLocaleString('pt-BR', {
    minimumFractionDigits: casas,
    maximumFractionDigits: casas,
  })} ${unidades[indice]}`;
}

/**
 * Data civil: so o dia, ou o dia gravado como meia-noite em UTC.
 *
 * Vigencia e validade sao datas civis. O servidor as devolve como "2026-10-19" ou
 * como "2026-10-19T00:00:00Z". Lidas com `new Date`, a meia-noite em UTC vira 21h
 * do dia anterior no horario de Brasilia, e a tela mostrava o dia errado
 * (inventario 47, anexo C). O dia sai dos digitos, sem fuso.
 */
const dataCivil = /^(\d{4})-(\d{2})-(\d{2})(?:T00:00:00(?:\.0+)?(?:Z|[+-]00:00))?$/;

export function formatDate(value: string | null | undefined): string {
  if (!value) return '';
  const civil = dataCivil.exec(value);
  if (civil) {
    const [, ano, mes, dia] = civil;
    return `${dia}/${mes}/${ano}`;
  }
  return dataCurta.format(new Date(value));
}

/**
 * Data civil lida pelos digitos: ano, mes e dia do comeco do texto, sem fuso.
 *
 * Vale para "2026-10-19", para a meia-noite em UTC que o servidor devolve nas colunas
 * `date` e para qualquer texto ISO que comece pela data. Devolve null quando o comeco
 * nao e uma data do calendario (mes 13, 30 de fevereiro).
 */
export function parseCivilDate(
  value: string | null | undefined,
): { year: number; month: number; day: number } | null {
  if (!value) return null;
  const partes = /^(\d{4})-(\d{2})-(\d{2})(?:$|T)/.exec(value);
  if (!partes) return null;
  const year = Number(partes[1]);
  const month = Number(partes[2]);
  const day = Number(partes[3]);
  if (month < 1 || month > 12 || day < 1) return null;
  // O ultimo dia do mes sai do proprio calendario, em UTC, onde nao ha horario de
  // verao nem fuso para deslocar o dia.
  const ultimoDia = new Date(Date.UTC(year, month, 0)).getUTCDate();
  if (day > ultimoDia) return null;
  return { year, month, day };
}

/**
 * Data civil no padrao brasileiro, sem conversao de fuso.
 *
 * Use para vigencia, validade, vencimento, "vigente desde", fundacao: o dia que a
 * pessoa escolheu, e nao um instante. `formatDate` acerta as duas formas que o
 * servidor devolve hoje; esta funcao deixa a intencao escrita e acerta tambem a data
 * que chegar com hora, lendo so o dia (Anexo C do 47: o card "Valores e prazos", a
 * apolice e a faixa de taxa mostravam o dia anterior no horario de Brasilia).
 */
export function formatCivilDate(value: string | null | undefined): string {
  const data = parseCivilDate(value);
  if (!data) return value ? value : '';
  const dois = (numero: number) => String(numero).padStart(2, '0');
  return `${dois(data.day)}/${dois(data.month)}/${String(data.year).padStart(4, '0')}`;
}

/** Fuso padrao da empresa (tenants.timezone nasce com ele). */
export const FUSO_PADRAO = 'America/Sao_Paulo';

/**
 * O dia de hoje como data civil ("2026-10-19") no fuso informado.
 *
 * Comparar vencimento com `new Date()` mistura instante e data civil: a 21h de
 * Brasilia ja e o dia seguinte em UTC, e uma apolice que vence hoje apareceria
 * vencida a noite (Anexo C do 47, "Apolice" 5). A comparacao certa e entre datas
 * civis, com o dia de hoje no fuso da empresa.
 */
export function todayCivil(
  timeZone: string = FUSO_PADRAO,
  now: Date = new Date(),
): string {
  const partes = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const parte = (tipo: string) => partes.find((p) => p.type === tipo)?.value ?? '';
  return `${parte('year')}-${parte('month')}-${parte('day')}`;
}

/**
 * Compara duas datas civis: negativo se a primeira e anterior, zero se iguais,
 * positivo se posterior. Data invalida e recusada com erro, para que um texto
 * errado nao vire "vencido" ou "em dia" por acidente.
 */
export function compareCivilDates(a: string, b: string): number {
  const x = parseCivilDate(a);
  const y = parseCivilDate(b);
  if (!x || !y) throw new Error(`Data civil invalida: ${!x ? a : b}`);
  return x.year - y.year || x.month - y.month || x.day - y.day;
}

/**
 * Informa se a data civil ja passou. O proprio dia ainda nao passou: o que vale
 * "ate" uma data vale o dia inteiro.
 */
export function isCivilDatePast(value: string, today: string = todayCivil()): boolean {
  return compareCivilDates(value, today) < 0;
}

/**
 * Nome por extenso de cada UF, como no catalogo do IBGE (P-FUN-1) e como o documento
 * da apolice imprime. E dado oficial, e por isso leva acento.
 */
const nomesDasUfs: Record<string, string> = {
  AC: 'Acre',
  AL: 'Alagoas',
  AP: 'Amapá',
  AM: 'Amazonas',
  BA: 'Bahia',
  CE: 'Ceará',
  DF: 'Distrito Federal',
  ES: 'Espírito Santo',
  GO: 'Goiás',
  MA: 'Maranhão',
  MT: 'Mato Grosso',
  MS: 'Mato Grosso do Sul',
  MG: 'Minas Gerais',
  PA: 'Pará',
  PB: 'Paraíba',
  PR: 'Paraná',
  PE: 'Pernambuco',
  PI: 'Piauí',
  RJ: 'Rio de Janeiro',
  RN: 'Rio Grande do Norte',
  RS: 'Rio Grande do Sul',
  RO: 'Rondônia',
  RR: 'Roraima',
  SC: 'Santa Catarina',
  SP: 'São Paulo',
  SE: 'Sergipe',
  TO: 'Tocantins',
};

/** As 27 siglas, em ordem alfabetica. Reserva do formulario quando o catalogo falha. */
export const SIGLAS_DAS_UFS: readonly string[] = Object.keys(nomesDasUfs).sort();

/** Nome por extenso da UF pela sigla; sigla desconhecida volta como veio. */
export function formatUf(code: string | null | undefined): string {
  if (!code) return '';
  return nomesDasUfs[code.trim().toUpperCase()] ?? code;
}

/**
 * Telefone brasileiro com a mascara usual, so para leitura.
 *
 * Fixo com DDD (10 digitos), celular com DDD (11), com o codigo do pais (12 ou 13,
 * comecando por 55), sem DDD (8 ou 9) e numero 0800, 0300 ou 0500 (11). Fora disso o
 * texto volta como veio: a mascara a forca esconderia o dado errado.
 */
export function formatPhone(value: string | null | undefined): string {
  if (!value) return '';
  let digitos = value.replace(/\D/g, '');
  let pais = '';
  if (/^0[358]00/.test(digitos) && digitos.length === 11) {
    return `${digitos.slice(0, 4)} ${digitos.slice(4, 7)} ${digitos.slice(7)}`;
  }
  if ((digitos.length === 12 || digitos.length === 13) && digitos.startsWith('55')) {
    pais = '+55 ';
    digitos = digitos.slice(2);
  }
  switch (digitos.length) {
    case 8:
      return `${digitos.slice(0, 4)}-${digitos.slice(4)}`;
    case 9:
      return `${digitos.slice(0, 5)}-${digitos.slice(5)}`;
    case 10:
      return `${pais}(${digitos.slice(0, 2)}) ${digitos.slice(2, 6)}-${digitos.slice(6)}`;
    case 11:
      return `${pais}(${digitos.slice(0, 2)}) ${digitos.slice(2, 7)}-${digitos.slice(7)}`;
    default:
      return value;
  }
}

/**
 * Valor em centavos (inteiro, como as colunas `*_cents`) no formato da moeda.
 *
 * Para quem recebe so o inteiro, sem o objeto de dinheiro do contrato. Valor que nao
 * e inteiro exato e recusado com texto vazio: centavo fracionado nao existe, e
 * arredondar aqui esconderia o erro de quem montou o numero.
 */
export function formatCents(
  cents: number | null | undefined,
  currency: string = BRL,
): string {
  if (cents === null || cents === undefined || !Number.isSafeInteger(cents)) return '';
  return formatMoney({ amount: cents, currency, exponent: expoenteDe(currency) });
}

/** Aplica a mascara de CPF (11 digitos) ou CNPJ (14 digitos), so para leitura. */
export function formatCpfCnpj(document: string): string {
  const digits = document.replace(/\D/g, '');
  if (digits.length === 11) {
    return digits.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
  }
  return formatDocument(document);
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '';
  return dataHora.format(new Date(value));
}

/** Aplica a mascara de CNPJ apenas para leitura. O dado guardado tem so digitos. */
export function formatDocument(document: string): string {
  const digits = document.replace(/\D/g, '');
  if (digits.length !== 14) return document;
  return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
}

/**
 * Tempo relativo curto, para a linha do tempo.
 *
 * Um historico com data absoluta em cada linha exige calcular a distancia de
 * cabeca. "ha 2 dias" responde a pergunta que a pessoa realmente faz.
 */
export function formatRelative(value: string): string {
  const alvo = new Date(value).getTime();
  const diff = Date.now() - alvo;
  const minutos = Math.round(diff / 60_000);

  if (minutos < 1) return 'agora';
  if (minutos < 60) return `ha ${minutos} min`;

  const horas = Math.round(minutos / 60);
  if (horas < 24) return `ha ${horas} h`;

  const dias = Math.round(horas / 24);
  if (dias < 30) return `ha ${dias} d`;

  return dataCurta.format(new Date(value));
}

/**
 * CNAE na grafia da CONCLA, 0000-0/00, como o cartao CNPJ e o OnPoint o mostram
 * (P-TOM-C1). O cadastro grava os sete digitos; codigo fora do formato volta como
 * veio, para nao esconder o CNAE antigo gravado em texto.
 */
export function formatCnae(code: string | null | undefined): string {
  const valor = (code ?? '').trim();
  if (!/^\d{7}$/.test(valor)) return valor;
  return `${valor.slice(0, 4)}-${valor.slice(4, 5)}/${valor.slice(5)}`;
}

/**
 * Documento de terceiro numa lista: o CPF sai mascarado como o servidor mascara
 * ("***.982.247-**"), porque e dado pessoal (LGPD); o CNPJ sai formatado, porque o
 * CNPJ de pessoa juridica nao e dado pessoal. Usado onde o OnPoint mascara o documento
 * do segurado (TOM-167, APO-007: InsuredDocumentMask).
 */
export function formatDocumentoDeTerceiro(document: string | null | undefined): string {
  const digitos = (document ?? '').replace(/\D/g, '');
  if (digitos.length === 11) {
    return `***.${digitos.slice(3, 6)}.${digitos.slice(6, 9)}-**`;
  }
  return digitos.length === 14 ? formatCpfCnpj(digitos) : (document ?? '');
}
